export type AccessFlow = "user" | "venue" | "admin";

export type SocialAuthProvider = "google" | "apple";

export const APP_ENTRY_ROUTE = "/";

const ACCESS_FLOW_LOCK_KEY = "zappy.access-flow.lock";
const SOCIAL_AUTH_PENDING_KEY = "zappy.social-auth.pending";
const SOCIAL_AUTH_PENDING_TTL_MS = 2 * 60 * 1000;

/**
 * Sanitizador único de redirects internos.
 *
 * Aceita APENAS rotas internas seguras (mesma origem, path-only). Bloqueia:
 * - valores vazios / não-string
 * - URLs absolutas (http:, https:, javascript:, data:, etc.)
 * - protocol-relative (`//evil.com`, `\\evil.com`)
 * - paths que começam com backslash ou contêm caracteres de controle
 * - `..` / segmentos de path traversal
 *
 * Retorna o path normalizado ou `null` se inseguro. É a ÚNICA função que
 * deve ser usada para validar redirects vindos de query string, storage,
 * deep links ou qualquer fonte externa.
 */
export const sanitizeInternalRedirect = (value?: string | null): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Caracteres de controle (inclui \n, \r, \t, NUL) nunca podem aparecer.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return null;

  // Precisa começar com "/" e NÃO pode ser "//..." nem "/\..." (protocol-relative
  // ou tentativas de escapar a origem).
  if (!trimmed.startsWith("/")) return null;
  if (trimmed.startsWith("//")) return null;
  if (trimmed.startsWith("/\\")) return null;
  if (trimmed.includes("\\")) return null;

  // Bloqueia tentativas de embutir esquema/host (ex.: "/http://evil").
  if (/^\/+(https?:|javascript:|data:|vbscript:|file:|about:)/i.test(trimmed)) return null;

  // Bloqueia path traversal.
  const segments = trimmed.split("/");
  if (segments.some((seg) => seg === "..")) return null;

  return trimmed;
};

export const getAppEntryUrl = (redirectTo?: string | null) => {
  const safeRedirect = sanitizeInternalRedirect(redirectTo);

  if (!safeRedirect || safeRedirect === APP_ENTRY_ROUTE) {
    return APP_ENTRY_ROUTE;
  }

  return `${APP_ENTRY_ROUTE}?${new URLSearchParams({ redirect: safeRedirect }).toString()}`;
};

export const getAuthEntryUrl = (flow: AccessFlow, redirectTo?: string | null) => {
  const params = new URLSearchParams({ role: flow });
  const safeRedirect = sanitizeInternalRedirect(redirectTo);

  if (safeRedirect) {
    params.set("redirect", safeRedirect);
  }

  return `/auth?${params.toString()}`;
};

const getSessionStorage = () => {
  if (typeof window === "undefined") return null;
  return window.sessionStorage;
};

const getLocalStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const isSocialAuthProvider = (value: unknown): value is SocialAuthProvider =>
  value === "google" || value === "apple";

const isAccessFlow = (value: unknown): value is AccessFlow =>
  value === "user" || value === "venue" || value === "admin";

/**
 * Lock de fluxo é replicado em sessionStorage (compat) e localStorage
 * (compartilhado entre abas). Qualquer cross-flow detectado em outra aba
 * dispara logout imediato via `storage` event no useAuth.
 */
export const getAccessFlowLock = (): AccessFlow | null => {
  const localValue = getLocalStorage()?.getItem(ACCESS_FLOW_LOCK_KEY);
  if (isAccessFlow(localValue)) return localValue;
  const sessionValue = getSessionStorage()?.getItem(ACCESS_FLOW_LOCK_KEY);
  return isAccessFlow(sessionValue) ? sessionValue : null;
};

export const lockAccessFlow = (flow: AccessFlow) => {
  getSessionStorage()?.setItem(ACCESS_FLOW_LOCK_KEY, flow);
  try { getLocalStorage()?.setItem(ACCESS_FLOW_LOCK_KEY, flow); } catch { /* ignore */ }
};

export const clearAccessFlowLock = () => {
  getSessionStorage()?.removeItem(ACCESS_FLOW_LOCK_KEY);
  try { getLocalStorage()?.removeItem(ACCESS_FLOW_LOCK_KEY); } catch { /* ignore */ }
};

export const ACCESS_FLOW_LOCK_STORAGE_KEY = ACCESS_FLOW_LOCK_KEY;

export const markSocialAuthPending = (provider: SocialAuthProvider) => {
  getSessionStorage()?.setItem(
    SOCIAL_AUTH_PENDING_KEY,
    JSON.stringify({ provider, createdAt: Date.now() })
  );
};

export const getSocialAuthPending = (): SocialAuthProvider | null => {
  const rawValue = getSessionStorage()?.getItem(SOCIAL_AUTH_PENDING_KEY);

  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue) as {
      provider?: unknown;
      createdAt?: unknown;
    };

    if (
      isSocialAuthProvider(parsed.provider) &&
      typeof parsed.createdAt === "number" &&
      Date.now() - parsed.createdAt <= SOCIAL_AUTH_PENDING_TTL_MS
    ) {
      return parsed.provider;
    }
  } catch {
    // Ignore malformed storage values and clear them below.
  }

  getSessionStorage()?.removeItem(SOCIAL_AUTH_PENDING_KEY);
  return null;
};

export const clearSocialAuthPending = () => {
  getSessionStorage()?.removeItem(SOCIAL_AUTH_PENDING_KEY);
};